import { useEffect, useMemo, useState } from 'react'
import { useBranch } from '../contexts/BranchContext'
import { washService } from '../services/firebase'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import LoadingSpinner from '../components/LoadingSpinner'
import { cn } from '@/lib/utils'
import { washPaymentBadgeClass, washPaymentLabel } from '@/utils/paymentStatus'

function asDate(ts) {
  if (!ts) return null
  if (typeof ts?.toDate === 'function') return ts.toDate()
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? null : d
}

function fmtDate(ts) {
  const d = asDate(ts)
  if (!d) return '—'
  return d.toLocaleString('en-ZA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function statusPill(status) {
  const s = String(status || '').toLowerCase()
  const map = {
    waiting: 'bg-amber-500/15 text-amber-900 dark:text-amber-100 border-amber-500/30',
    washing: 'bg-sky-500/15 text-sky-900 dark:text-sky-100 border-sky-500/30',
    drying: 'bg-violet-500/15 text-violet-900 dark:text-violet-100 border-violet-500/30',
    polishing: 'bg-fuchsia-500/15 text-fuchsia-900 dark:text-fuchsia-100 border-fuchsia-500/30',
    interior: 'bg-cyan-500/15 text-cyan-900 dark:text-cyan-100 border-cyan-500/30',
    ready: 'bg-emerald-500/15 text-emerald-900 dark:text-emerald-100 border-emerald-500/30',
    picked_up: 'bg-emerald-700/15 text-emerald-950 dark:text-emerald-100 border-emerald-700/30',
    completed: 'bg-emerald-700/15 text-emerald-950 dark:text-emerald-100 border-emerald-700/30',
    cancelled: 'bg-destructive/10 text-destructive border-destructive/30',
  }
  return map[s] || 'bg-muted text-muted-foreground border-border'
}

export default function WashHistoryPage() {
  const { currentBranch, isViewingAllBranches } = useBranch()
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [queryText, setQueryText] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 25

  useEffect(() => {
    async function load() {
      if (!currentBranch) return
      setLoading(true)
      try {
        const branchId = isViewingAllBranches() ? 'all-branches' : currentBranch.id
        const washes = await washService.getWashesByBranch(branchId)
        setRows(washes || [])
      } catch (e) {
        console.error('Failed to load wash history:', e)
        setRows([])
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [currentBranch, isViewingAllBranches])

  const filtered = useMemo(() => {
    const q = queryText.trim().toLowerCase()
    const base = [...rows].sort((a, b) => {
      const da = asDate(a?.createdAt)?.getTime() || 0
      const db = asDate(b?.createdAt)?.getTime() || 0
      return db - da
    })
    if (!q) return base
    return base.filter((w) => {
      const plate = w?.carDetails?.licensePlate || ''
      const make = w?.carDetails?.make || ''
      const model = w?.carDetails?.model || ''
      const color = w?.carDetails?.color || ''
      const service = w?.serviceType || ''
      const name = w?.customerName || ''
      const phone = w?.customerPhone || ''
      const status = w?.status || ''
      const payment = w?.paymentStatus || ''
      const branch = w?.branchName || ''
      return [plate, make, model, color, service, name, phone, status, payment, branch].some((v) =>
        String(v).toLowerCase().includes(q)
      )
    })
  }, [rows, queryText])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const start = (safePage - 1) * pageSize
  const pageRows = filtered.slice(start, start + pageSize)

  useEffect(() => {
    setPage(1)
  }, [queryText])

  if (loading) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="border-b border-border pb-6">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Operations</p>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Wash history</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          View past washes across {isViewingAllBranches() ? 'all branches' : currentBranch?.name || 'branch'}.
        </p>
        <div className="mt-4 max-w-xl">
          <Input
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            placeholder="Search: reg / customer / service / status..."
            className="h-10"
          />
        </div>
      </div>

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">Washes</CardTitle>
            <p className="text-xs text-muted-foreground">
              Showing {pageRows.length} of {filtered.length}
            </p>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 text-left font-medium">Date</th>
                  <th className="py-2 text-left font-medium">Customer</th>
                  <th className="py-2 text-left font-medium">Vehicle</th>
                  <th className="py-2 text-left font-medium">Reg</th>
                  <th className="py-2 text-left font-medium">Service</th>
                  <th className="py-2 text-left font-medium">Status</th>
                  <th className="py-2 text-left font-medium">Paid</th>
                  <th className="py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-muted-foreground">
                      No washes found.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((w) => (
                    <tr key={w.id} className="hover:bg-muted/30">
                      <td className="py-3 pr-3 whitespace-nowrap">{fmtDate(w.createdAt)}</td>
                      <td className="py-3 pr-3">
                        <div className="font-medium text-foreground">{w.customerName || 'Walk-in'}</div>
                        {w.customerPhone ? (
                          <div className="text-xs text-muted-foreground">{w.customerPhone}</div>
                        ) : null}
                      </td>
                      <td className="py-3 pr-3 whitespace-nowrap">
                        {w?.carDetails?.make || '—'} {w?.carDetails?.model || ''}
                        {w?.carDetails?.color ? (
                          <span className="text-xs text-muted-foreground"> · {w.carDetails.color}</span>
                        ) : null}
                      </td>
                      <td className="py-3 pr-3 whitespace-nowrap font-medium">
                        {w?.carDetails?.licensePlate || '—'}
                      </td>
                      <td className="py-3 pr-3">{w.serviceType || '—'}</td>
                      <td className="py-3 pr-3">
                        <Badge variant="outline" className={cn('font-medium capitalize', statusPill(w.status))}>
                          {String(w.status || '—').replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-3 pr-3">
                        <span className={cn('text-xs font-semibold', washPaymentBadgeClass(w))}>
                          {washPaymentLabel(w)}
                        </span>
                      </td>
                      <td className="py-3 text-right tabular-nums font-semibold">
                        R{Number(w.amount || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <div className="text-xs text-muted-foreground">
              Page <span className="font-medium text-foreground">{safePage}</span> of{' '}
              <span className="font-medium text-foreground">{totalPages}</span>
            </div>
            <button
              className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

